import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Percent, DollarSign } from "lucide-react";
import { adminTokenStore } from "@/services/sellerTokenStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Attach the memory-held admin JWT + withCredentials so the cookie rides
// along too. Central helper so the 4 axios calls below stay tidy.
const adminAxiosConfig = () => {
  const token = adminTokenStore.getAccessToken();
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

const CouponManager = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    max_uses: "",
    expires_at: ""
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`, adminAxiosConfig());
      setCoupons(response.data.coupons);
    } catch (error) {
      toast.error("Failed to fetch coupons");
      console.error(error);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.discount_value) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };

      await axios.post(`${API}/admin/coupons`, payload, adminAxiosConfig());

      toast.success("Coupon created successfully!");
      setFormData({
        code: "",
        discount_type: "percentage",
        discount_value: "",
        max_uses: "",
        expires_at: ""
      });
      setShowForm(false);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!confirm(`Delete coupon ${code}?`)) return;

    try {
      await axios.delete(`${API}/admin/coupons/${code}`, adminAxiosConfig());
      toast.success("Coupon deleted!");
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to delete coupon");
      console.error(error);
    }
  };

  const handleToggleCoupon = async (code) => {
    try {
      await axios.patch(`${API}/admin/coupons/${code}/toggle`, {}, adminAxiosConfig());
      toast.success("Coupon status updated!");
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to update coupon");
      console.error(error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Create Coupon Card */}
      <Card className="glass border-0 shadow-xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-2xl">
              🎫 Discount Coupons
            </CardTitle>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              {showForm ? "Cancel" : "New Coupon"}
            </Button>
          </div>
        </CardHeader>
        
        {showForm && (
          <CardContent>
            <form onSubmit={handleCreateCoupon} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code">Coupon Code *</Label>
                  <Input
                    id="code"
                    placeholder="SUMMER2025"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="discount_type">Discount Type *</Label>
                  <Select
                    value={formData.discount_type}
                    onValueChange={(value) => setFormData({ ...formData, discount_type: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">
                        <div className="flex items-center gap-2">
                          <Percent className="w-4 h-4" />
                          Percentage
                        </div>
                      </SelectItem>
                      <SelectItem value="fixed">
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          Fixed Amount
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="discount_value">
                    Discount Value * {formData.discount_type === "percentage" ? "(0-100%)" : "($)"}
                  </Label>
                  <Input
                    id="discount_value"
                    type="number"
                    step="0.01"
                    placeholder={formData.discount_type === "percentage" ? "20" : "15.00"}
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="max_uses">Max Uses (Optional)</Label>
                  <Input
                    id="max_uses"
                    type="number"
                    placeholder="Unlimited"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="expires_at">Expiration Date (Optional)</Label>
                  <Input
                    id="expires_at"
                    type="datetime-local"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
              >
                {loading ? "Creating..." : "Create Coupon"}
              </Button>
            </form>
          </CardContent>
        )}
      </Card>

      {/* Coupons List */}
      <Card className="glass border-0 shadow-xl">
        <CardHeader>
          <CardTitle>Active Coupons ({coupons.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">No coupons created yet</p>
              <p className="text-sm mt-2">Click "New Coupon" to create your first discount code</p>
            </div>
          ) : (
            <div className="space-y-4">
              {coupons.map((coupon) => (
                <div
                  key={coupon.code}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-lg text-purple-600 bg-purple-50 px-3 py-1 rounded">
                        {coupon.code}
                      </span>
                      {!coupon.active && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-6 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        {coupon.discount_type === "percentage" ? (
                          <>
                            <Percent className="w-4 h-4" />
                            {coupon.discount_value}% off
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-4 h-4" />
                            ${coupon.discount_value} off
                          </>
                        )}
                      </span>
                      
                      <span>
                        Uses: {coupon.uses_count} / {coupon.max_uses || "∞"}
                      </span>
                      
                      <span>
                        Expires: {formatDate(coupon.expires_at)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={coupon.active}
                      onCheckedChange={() => handleToggleCoupon(coupon.code)}
                    />
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteCoupon(coupon.code)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CouponManager;
